import { createRootRouteWithContext, Outlet } from '@tanstack/react-router';
import { TanStackRouterDevtools } from '@tanstack/react-router-devtools';
import type { QueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/stores/authStore';
import { LoadingScreen } from '@/components/common/LoadingScreen';
import { ErrorScreen } from '@/components/common/ErrorScreen';

/**
 * Router Context
 * QueryClient を router context として共有
 */
export interface RouterContext {
  queryClient: QueryClient;
}

/**
 * Root Route
 * TanStack Router のルートルート
 * beforeLoad で認証初期化を行う
 *
 * OAuth 認証後の処理は index.tsx の beforeLoad で行う
 */
export const Route = createRootRouteWithContext<RouterContext>()({
  beforeLoad: async () => {
    // Zustand store から auth state を取得
    const authStore = useAuthStore.getState();

    // 常にバックエンドでセッション検証を行う
    // localStorage の永続化状態は stale になる可能性があるため、
    // アプリ起動時に必ずバックエンドと同期する
    const authResult = await authStore.initializeAuth();
    if (!authResult.success && authResult.error) {
      // 認証失敗は初回ユーザーや期限切れセッションでは想定内
      console.warn('Initial auth check failed:', authResult.error);
    }
  },
  component: RootComponent,
  pendingComponent: () => <LoadingScreen message="Loading..." />,
  errorComponent: ({ error, reset }) => (
    <ErrorScreen error={error} onReset={reset} />
  ),
});

/**
 * Root Component
 * すべてのルートの親コンポーネント
 */
function RootComponent() {
  return (
    <>
      <Outlet />
      {import.meta.env.DEV && <TanStackRouterDevtools position="bottom-left" />}
    </>
  );
}
